import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { UntypedFormControl, Validators } from '@angular/forms';
import { SelectOption } from '@df/design-system-components';
import { MaterialsService } from 'src/app/providers/services/materials.service';
import { SkidsService } from 'src/app/providers/services/skids.service';
import { Mission } from 'src/app/storage/models/mission.model';
import { SkidMission } from 'src/app/storage/models/skid-mission.model';
import { Skid } from 'src/app/storage/models/skid.model';
import { ITrain } from 'src/app/storage/interfaces/train.interface';

export interface SkidSelection {
  skid: Skid;
  trainpk: number;
}

@Component({
    selector: 'app-add-skid',
    templateUrl: './add-skid.component.html',
    styleUrls: ['./add-skid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddSkidComponent implements OnInit, OnDestroy {
  displayedSkids: Skid[];
  skids: Skid[];
  missionSkids: SkidMission[] = [];
  searchValue = '';

  @Input() mission: Mission;
  @Input() missionSkid: SkidMission;
  @Input() missionTrains: ITrain[] = [];
  @Output() closeModal = new EventEmitter<SkidSelection>();

  selectedTrainFC = new UntypedFormControl(null, Validators.required);
  trainOptions: SelectOption<number>[] = [];
  showTrainSelector = true;

  private skidsSubscription: Subscription;
  private trainSubscription: Subscription;

  constructor(
    private skidsService: SkidsService,
    private materialsService: MaterialsService
  ) { }

  ngOnInit(): void {
    const availableTrains = this.missionTrains ?? [];
    this.trainOptions = availableTrains.map(train => ({
      value: train.trainpk,
      label: train.trainName || train.trainId
    }));
    const onlyDefaultTrain = availableTrains.length === 1
      && availableTrains[0].trainId?.trim().toUpperCase() === 'DEFAULT';
    this.showTrainSelector = !onlyDefaultTrain;
    if (onlyDefaultTrain) {
      this.selectedTrainFC.setValue(availableTrains[0].trainpk, {emitEvent: false});
    }
    this.skidsSubscription = this.skidsService.skids$.subscribe((skids: Skid[]) => {
      this.missionSkids = this.skidsService.missionSkids$.getValue() ?? [];
      this.skids = [...(skids ?? [])]
        // Order skids by attributes
        .sort((a, b) => {
        return a.pumpMaxFlowrate === b.pumpMaxFlowrate ?
                ( a.pumpMaxPressure > b.pumpMaxPressure ? 1 : -1) :
                ( a.pumpMaxFlowrate > b.pumpMaxFlowrate ? 1 : -1 );
        });

      this.materialsService.setSkidsEquipments(this.skids);
      this._refreshDisplayedSkids();
    });
    this.trainSubscription = this.selectedTrainFC.valueChanges.subscribe(() => this._refreshDisplayedSkids());
  }

  ngOnDestroy(): void {
    if (this.skidsSubscription) { this.skidsSubscription.unsubscribe(); }
    if (this.trainSubscription) { this.trainSubscription.unsubscribe(); }
  }

  close(): void {
    this.closeModal.emit();
  }

  addSkid(skid: Skid): void {
    const trainpk = Number(this.selectedTrainFC.value);
    if (!Number.isFinite(trainpk) || trainpk <= 0) { return; }
    this.closeModal.emit({skid, trainpk});
  }

  filterSkids(value: string): void {
    this.searchValue = value ?? '';
    this._refreshDisplayedSkids();
  }

  private _refreshDisplayedSkids(): void {
    const selectedTrainpk = Number(this.selectedTrainFC.value);
    this.displayedSkids = (this.skids ?? []).filter(skid => {
      const alreadyAssignedToAnotherTrain = this.missionSkids.some(missionSkid =>
        missionSkid.skidId === skid.skidId
        && missionSkid.trainpk != null
        && missionSkid.trainpk !== selectedTrainpk);
      if (alreadyAssignedToAnotherTrain) { return false; }

      // List attribute to filter on
      const attributesToFilterOn = [
        skid.skidName
      ];

      // Find at least one attribute that checks the criterion
      return attributesToFilterOn.find(attribute =>
        attribute.toUpperCase().includes(this.searchValue.toUpperCase())
      ) != null;
    });
  }

  isNoDigital(skid: Skid): boolean {
    return !(skid.hasPump) && !(skid.hasGateway) && !(skid.hasFlowmeter);
  }

}
